import { useState, useEffect, useCallback } from "react";
import { logUserToMixpanel } from "../utils/mixpanel";
import Cookies from "js-cookie";
import { useTrainingProgramQuery } from "./useTrainingProgramQuery";
import { useAPI } from "./useAPI";

export function useAuthData(apiBaseUrl, enabled = true) {
  const [authToken, setAuthToken] = useState("");
  const [loggedInUser, setLoggedInUser] = useState({});
  const [trainingProgram, setTrainingProgram] = useState(null);
  const [allTrainingPrograms, setAllTrainingPrograms] = useState(null);

  const { callAPI } = useAPI();

  const currentPath = window.location.pathname.toLowerCase();
  const is2FA = currentPath.includes("2fa");

  // Use React Query for training programs
  const {
    data: trainingProgramsData,
    isLoading: isLoadingPrograms,
    error: programsError,
  } = useTrainingProgramQuery(apiBaseUrl, authToken, enabled);

  // Helper to save token and clear user state
  const saveAuthToken = useCallback((token) => {
    const isSecure = window.location.protocol === "https:";
    Cookies.set("token", token, {
      expires: 7,
      secure: isSecure,
      sameSite: isSecure ? "None" : "Lax",
    });
    setAuthToken(token);
    setLoggedInUser({});
  }, []);

  // 1. Load Token on Mount
  useEffect(() => {
    const token = Cookies.get("token");
    if (token && token.length > 0) {
      setAuthToken(token);
    }
  }, []);

  const fetchUser = () => {
    if (!enabled || !authToken || !apiBaseUrl || is2FA) return;

    const controller = new AbortController();
    const signal = controller.signal;

    try {
      const headers = { Authorization: `Token ${authToken}` };

      // Only fetch user data - training programs handled by React Query
      const userPromise = callAPI(`user`, { headers, signal });

      userPromise
        .then((userData) => {
          logUserToMixpanel(userData);
          if (Object.keys(userData).includes("email")) {
            setLoggedInUser(userData);
          } else {
            setLoggedInUser({});
            throw new Error("Invalid user");
          }
        })
        .catch((error) => {
          if (error.name !== "AbortError" && error.message !== "Invalid user") {
            console.error("Error fetching user:", error);
          }
        });
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching auth data:", error);
      }
    }
  };

  // Update training program from React Query data
  useEffect(() => {
    if (
      trainingProgramsData &&
      Array.isArray(trainingProgramsData) &&
      trainingProgramsData.length > 0
    ) {
      setTrainingProgram(trainingProgramsData[0]);
      setAllTrainingPrograms(trainingProgramsData);
    }
  }, [trainingProgramsData]);

  // 2. Fetch User & Programs when Token/URL exists
  useEffect(() => {
    if (!enabled) return;
    fetchUser();
  }, [enabled, authToken, apiBaseUrl, saveAuthToken]);

  return {
    authToken,
    saveAuthToken,
    loggedInUser,
    trainingProgram,
    allTrainingPrograms,
    fetchUser,
  };
}
